import { cloneDeep } from "lodash";
import {
  fetchAllSitesForUser,
  fetchHoursReport,
  fetchSite,
  fetchSiteBillingPortal,
  fetchSiteContent,
  fetchSiteHoursReport,
  fetchSitePaymentReport,
  fetchSiteSubscription,
  fetchUser,
  getSiteWebsiteReport,
  queryAllSubmissions,
  updateSite,
  updateSubmission,
  updateUserAccessToSite
} from "../../clients/bettercallkyle/BckApi";
import SitesStore from "../../stores/internal/SitesStore";
import { toJS } from "mobx";
import { fetchLiteAnalytics, fetchSiteAvaAnalytics, fetchSiteTrendingReport, postContentPublishRequest, postContentSubmission, postLightsailConnection, postLightsailConnectionTest } from "../../clients/bettercallkyle/BckAvaApi";
import UsersStore from "../../stores/internal/UsersStore";
import { postSubmission } from "../external/SubmissionActions";
import { isAdmin } from "../profile/UserActions";
import { getAllSubmissionsForAdmin } from "./AdminActions";

export const getAllSitesForUser = async (user_id, force_refresh = false) => {
  if (!force_refresh && SitesStore.sites !== undefined) {
    return cloneDeep({ sites: SitesStore.sites });
  }
  if (user_id === undefined) {
    throw new Error("No user id provided");
  }
  const response = await fetchAllSitesForUser(user_id);
  SitesStore.setSites(response.sites);
  return response;
}

// MORE SECURE
export const getSiteByIdForUser = async (site_id, user_id, force_refresh = false) => {
  const sitesById = toJS(SitesStore.siteById)
  if (!sitesById[site_id] || force_refresh) {
    const site = await fetchSite(site_id, user_id);
    SitesStore.setSite(site);
    return site
  } else {
    return sitesById[site_id]
  }
}

export const getUserByLoginIdForAdmin = async (user_login_id, user_id, force_refresh = false) => {
  const usersByLoginId = toJS(UsersStore.usersByLoginId)
  if (!usersByLoginId[user_login_id] || force_refresh) {
    const user = await fetchUser(user_login_id, user_id);
    UsersStore.setUser(user);
    return user;
  } else {
    return usersByLoginId[user_login_id]
  }
}

// DEPRECATED - use getSiteByIdForUser
export const getSiteById = async (site_id, force_refresh = false) => {
  const sitesById = toJS(SitesStore.siteById)
  if (!sitesById[site_id] || force_refresh) {
    const site = await fetchSite(site_id);
    sitesById[site_id] = site;
    SitesStore.setSite(sitesById);
    return site
  } else {
    return sitesById[site_id]
  }
}

export const getSiteAnalytics = async (site_id, force_refresh = false) => {
  if (!force_refresh
    && SitesStore.site_analytics[site_id] !== undefined) {
      return cloneDeep(SitesStore.site_analytics[site_id])
  }
  const analytics = await getSiteWebsiteReport(site_id);
  SitesStore.setSiteAnalytics(site_id, analytics);
  return analytics;
}

export const getQuickSiteAnalytics = async (user_id, site_id, options, date_range, force_refresh = false) => {
  if (!force_refresh
    && SitesStore.site_analytics_lite[site_id] !== undefined
    && SitesStore.site_analytics_lite[site_id][date_range] !== undefined) {
      return cloneDeep(SitesStore.site_analytics_lite[site_id][date_range])
  }
  const analytics = await fetchLiteAnalytics(user_id, site_id, options);
  SitesStore.setSiteAnalyticsLite(site_id, analytics, date_range);
  return analytics;
}

export const getSiteAnalyticsV2 = async (user_id, site_id, options, date_range, force_refresh = false) => {
  if (!force_refresh
    && SitesStore.site_analytics_v2[site_id] !== undefined
    && SitesStore.site_analytics_v2[site_id][date_range] !== undefined) {
      return cloneDeep(SitesStore.site_analytics_v2[site_id][date_range])
  }
  const analytics = await fetchSiteAvaAnalytics(user_id, site_id, options);
  SitesStore.setSiteAnalyticsV2(site_id, analytics, date_range);
  return analytics;
}

export const getSiteTrending = async (user_id, site_id, options, date_range, force_refresh = false) => {
  if (!force_refresh
    && SitesStore.site_trending[site_id] !== undefined
    && SitesStore.site_trending[site_id][date_range] !== undefined) {
      return cloneDeep(SitesStore.site_trending[site_id][date_range])
  }
  const trending = await fetchSiteTrendingReport(user_id, site_id, options);
  SitesStore.setSiteTrending(site_id, trending, date_range);
  return trending;
}

export const getSiteHoursReport = async (site_id, offset = 0, force_refresh = false) => {
  let key = `${site_id}`;
  let timeKey = `${offset}`
  if (!force_refresh
    && SitesStore.site_hour_reports[key] !== undefined
    && SitesStore.site_hour_reports[key][timeKey] !== undefined) {
    return cloneDeep(SitesStore.site_hour_reports[key][timeKey])
  }
  const report = await fetchHoursReport(site_id, offset);
  SitesStore.setSiteHourReport(key, timeKey, report);
  return report;
}

export const getSitePaymentReport = async (site_id, force_refresh = false) => {
  try {
    if (!force_refresh && SitesStore.site_invoice_reports[site_id] !== undefined) {
      return cloneDeep(SitesStore.site_invoice_reports[site_id])
    }
    const report = await fetchSitePaymentReport(site_id)
    SitesStore.setSiteInvoiceReport(site_id, report);
    return report;
  } catch (err) {
    const error = await err;
    throw error
  }
}

export const getSiteSubscription = async (user_id, site_id, force_refresh = false) => {
  try {
    if (!force_refresh && SitesStore.site_subscriptions[site_id] !== undefined) {
      return cloneDeep(SitesStore.site_subscriptions[site_id])
    }
    const response = await fetchSiteSubscription(user_id, site_id);
    SitesStore.setSiteSubscriptionReport(site_id, response);
    return response;
  } catch (err) {
    return undefined;
  }
}

export const getSiteContent = async (user_id, site_id, force_refresh = false) => {
  try {
    if (!force_refresh && SitesStore.site_content[site_id] !== undefined) {
      return cloneDeep(SitesStore.site_content[site_id])
    }
    const response = await fetchSiteContent(user_id, site_id);
    SitesStore.setSiteContent(site_id, response);
    return response;
  } catch (err) {
    return undefined;
  }
}

export const getSiteContentArticle = async (user_id, site_id, tracking_id, force_refresh = false) => {
  try {
    if (!force_refresh && SitesStore.site_content_article[tracking_id] !== undefined) {
      return cloneDeep(SitesStore.site_content_article[tracking_id])
    }
    const response = await fetchSiteContent(user_id, site_id, { tracking_id });
    const article = response[0];

    SitesStore.setSiteContentArticle(tracking_id, response[0]);
    return response;
  } catch (err) {
    return undefined;
  }
}

export const getSiteBillingPortal = async (user_id, site_id, force_refresh = false) => {
  try {
    if (!force_refresh && SitesStore.site_billing_portal[site_id] !== undefined) {
      return cloneDeep(SitesStore.site_billing_portal[site_id])
    }
    const response = await fetchSiteBillingPortal(user_id, site_id);
    SitesStore.setSiteBillingPortal(site_id, response);
    return response;
  } catch (err) {
    return undefined;
  }
}


export const updateSiteAccess = async (site_id, user_id, removed_users, added_users) => {
  await updateUserAccessToSite(site_id, user_id, removed_users, added_users);
  await getSiteByIdForUser(site_id, user_id, true)
}

export const updateSiteInformation = async (site_id, user_id, masks, maskProps) => {
  await updateSite(site_id, user_id, masks, maskProps)
  await getSiteByIdForUser(site_id, user_id, true)
}

export const getSiteSubmissions = async (site_id, force_refresh = false) => {
  try {
    if (!force_refresh && SitesStore.site_submissions[site_id] !== undefined) {
      return cloneDeep(SitesStore.site_submissions[site_id])
    }
    const response = await queryAllSubmissions(undefined, site_id, true);
    SitesStore.setSiteSubmissions(site_id, response || []);
    return response;
  } catch (err) {
    const error = await err;
    throw error;
  }
}

export const updateSiteSubmission = async (user_id, site_id, submission_id, masks, maskProps, options = {}) => {
  await updateSubmission(submission_id, user_id, masks, maskProps, options);
  await getSiteSubmissions(site_id, true);
  if (isAdmin()) await getAllSubmissionsForAdmin(user_id, true, true);
}

export const addSiteSubmission = async (user_id, site_id, requestType, values) => {
  await postSubmission(requestType, { user_id, site_id }, values, [], { send_submission_notification: true });
  await getSiteSubmissions(site_id, true);
  if (isAdmin()) await getAllSubmissionsForAdmin(user_id, true, true);
}

export const connectSiteLightsailConnection = async (user_id, site_id, lightsail) => {
  const resp = await postLightsailConnection(user_id, site_id, lightsail);
  await getSiteByIdForUser(site_id, user_id, true);
  return resp;
}

export const submitSiteContent = async (user_id, site_id, content, options) => {
  await postContentSubmission(user_id, site_id, content, options);
  await getSiteContent(user_id, site_id, true);
}

export const publishSiteContent = async (user_id, site_id, content_id, options) => {
  await postContentPublishRequest(user_id, site_id, content_id, options);
  await getSiteContent(user_id, site_id, true);
}
